/**
 * Confirmation dialog — admin revokes a user's access (soft-delete).
 *
 * Requires the admin to type the user's email to confirm — guards against
 * accidental clicks. Backend rejects self-revoke (400) and last-admin (409);
 * those errors surface as toasts.
 */
import * as Dialog from "@radix-ui/react-dialog";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { extractApiError } from "../../lib/api";
import { revokeUserAccess } from "../../lib/dataFetchers";
import { Button } from "../ui/Button";

interface Props {
  userId: string;
  userEmail: string;
  userName: string | null;
  open: boolean;
  onClose: () => void;
  onRevoked: () => void;
}

export function RevokeUserConfirmDialog({
  userId,
  userEmail,
  userName,
  open,
  onClose,
  onRevoked,
}: Props): JSX.Element {
  const qc = useQueryClient();
  const { t } = useTranslation(["admin", "common"]);
  const [typed, setTyped] = useState("");

  const mutation = useMutation({
    mutationFn: () => revokeUserAccess(userId),
    onSuccess: () => {
      toast.success(
        t("admin:user_revoked", { defaultValue: "Access revoked" }),
      );
      void qc.invalidateQueries({ queryKey: ["admin-users"] });
      setTyped("");
      onRevoked();
    },
    onError: (err: unknown) => {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 400) {
        toast.error(
          t("admin:cannot_revoke_self", {
            defaultValue: "Cannot revoke your own access.",
          }),
        );
      } else if (status === 409) {
        toast.error(
          t("admin:cannot_revoke_last_admin", {
            defaultValue: "Cannot revoke the last remaining admin.",
          }),
        );
      } else {
        toast.error(extractApiError(err));
      }
    },
  });

  function handleClose(): void {
    setTyped("");
    onClose();
  }

  const valid = typed.trim().toLowerCase() === userEmail.trim().toLowerCase();

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && handleClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-[min(92vw,460px)] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-surface border border-border p-6 shadow-xl focus:outline-none"
          onPointerDownOutside={(e) => mutation.isPending && e.preventDefault()}
        >
          <Dialog.Title className="text-[16px] font-semibold text-text mb-1">
            {t("admin:revoke_user_access_title", {
              defaultValue: "Revoke user access",
            })}
          </Dialog.Title>
          <Dialog.Description className="text-[13px] text-dim mb-4">
            {t("admin:revoke_user_access_body", {
              defaultValue:
                "This user will be signed out immediately and will see an access-denied page on any future sign-in attempt. The action is reversible — re-invite via the Invitations card to restore access.",
            })}
          </Dialog.Description>

          <div className="bg-surface-2 border border-border rounded-lg p-3 mb-4">
            <div className="text-[13px] font-medium text-text">
              {userName || userEmail}
            </div>
            <div className="text-[12px] text-dim">{userEmail}</div>
          </div>

          <label className="block">
            <span className="block text-[12.5px] font-medium text-text mb-1">
              {t("admin:revoke_confirm_label", {
                defaultValue: "Type the email to confirm",
              })}
            </span>
            <input
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoFocus
              autoComplete="off"
              className="w-full rounded-lg border border-border bg-surface-2 px-3 py-2 text-base focus:bg-surface focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/14"
            />
          </label>

          <div className="mt-5 flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={handleClose}
              disabled={mutation.isPending}
            >
              {t("common:cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button
              type="button"
              variant="danger-soft"
              onClick={() => mutation.mutate()}
              disabled={!valid || mutation.isPending}
            >
              {mutation.isPending
                ? t("common:revoking", { defaultValue: "Revoking..." })
                : t("admin:revoke_access", {
                    defaultValue: "Revoke access",
                  })}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
